import React, { useState } from 'react';

import isEqual from 'lodash/isEqual';

import {
  isMobileResolution,
  urlPatternValidation,
} from '../../../infrastructure/utils/common';
import ActivitySectionButtons from '../ActivitySectionButtons';
import {
  Activity,
  ActivitySource,
  ExternalSourceMetadata,
} from '../../../types/Activity';
import { Vendor } from '../../../types/Vendor';
import Disable from "../../utils/Disable";

import ActivitySourceFieldsMobile from './ActivitySourceFieldsMobile';
import { ActivitiesSourceForm } from './ActivitiesSourceForm';
import { isConnectedStripeAccount } from './helpers';

const getFieldsFromActivity = (activity: Activity) => ({
  commission: activity.commission,
  source: activity.source,
  metadata: activity.metadata,
});

type Props = {
  activity: Activity;
  vendor: Vendor | null;
  updateActivity(fields): Promise<Activity | undefined>;
  disabled: boolean;
};

const ActivitySourceFields = ({ updateActivity, activity, vendor, disabled }: Props) => {
  const [initialFields, setInitialFields] = useState<Partial<Activity>>(
    getFieldsFromActivity(activity)
  );
  const [fields, setFields] = useState(getFieldsFromActivity(activity));
  const [showErrors, setShowErrors] = useState(false);

  const { source, metadata } = fields;

  const onChange = (key, value) => {
    if (key === 'source') {
      setFields(currentFields => ({
        ...currentFields,
        source: value,
        metadata: undefined,
      }));
    } else {
      setFields(currentFields => ({
        ...currentFields,
        [key]: value,
      }));
    }
  };

  const validExternalUrlMetadata = () => {
    const metadata = (fields.metadata as ExternalSourceMetadata) || undefined;
    return urlPatternValidation(metadata?.externalUrl);
  };

  const validSourceMetadata = () => {
    const metadata = fields.metadata || undefined;
    return metadata?.stripeId && fields.commission;
  };

  const onSaveClick = async event => {
    event.preventDefault();

    if (source === ActivitySource.externalUrl) {
      if (!validExternalUrlMetadata()) {
        alert('Please ensure the Source form is filled out correctly');
        setShowErrors(true);
        return;
      }
    }

    if (isConnectedStripeAccount(source)) {
      if (!validSourceMetadata()) {
        alert('Please ensure the Source form is filled out correctly');
        setShowErrors(true);
        return;
      }
    }

    setShowErrors(false);

    const result = await updateActivity({
      ...fields,
      commission: activity.commission,
    });

    if (result) {
      setInitialFields(result);
    }
  };

  const werePropertiesChanged = () => !isEqual(fields, initialFields);

  const { stripeConnectedAccounts = [] } = vendor || {};

  if (isMobileResolution()) {
    return (
      <ActivitySourceFieldsMobile
        stripeAccounts={stripeConnectedAccounts || []}
        commission={activity.commission}
        disabled={!werePropertiesChanged()}
        metadata={metadata}
        onChange={onChange}
        onSaveClick={onSaveClick}
        showErrors={showErrors}
        source={source}
      />
    );
  }

  return (
    <Disable disabled={disabled} className='data-box'>
      <div className='activity-section-top'>
        <h4 className='header'>Source</h4>
        <ActivitySectionButtons
          disabled={!werePropertiesChanged()}
          onSaveClick={onSaveClick}
        />
      </div>
      <ActivitiesSourceForm
        stripeAccounts={stripeConnectedAccounts || []}
        commission={activity.commission}
        metadata={metadata}
        onChange={onChange}
        showErrors={showErrors}
        source={source}
        deadline={activity.deadline}
        disabled={disabled}
      />
    </Disable>
  );
};

export default React.memo(ActivitySourceFields);
